import React, { useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import type { RootStackParamList } from "../navigation/navigation.types";
import { useAppConfig } from "../AppConfigProvider";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";

const noAuth = (WrappedComponent) => {
  const NoAuthComponent = (props) => {
    const { isUpdating, isAuthenticated, isEmailVerified, otpVerifiedForSession, userInfo, isAuthReady } = useAuth();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();
    const { isCompanionMode, allowFormSignup, courseModernizerEnabled, courseModernizerOnlyEnabled } = useAppConfig();
    const isVerified = isEmailVerified && otpVerifiedForSession;
    const hasAuthCallbackTokens = (() => {
      if (route.name !== "LoginLTI" && route.name !== "LoginSSO") {
        return false;
      }
      const params = route.params as { accessToken?: string; refreshToken?: string } | undefined;
      if (params?.accessToken && params?.refreshToken) {
        return true;
      }
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.has("accessToken") && searchParams.has("refreshToken");
      }
      return false;
    })();

    useFocusEffect(
      useCallback(() => {
        if (hasAuthCallbackTokens) {
          return;
        }
        if (!isAuthReady || (isUpdating && !hasAuthCallbackTokens)) {
          return;
        }

        if (!allowFormSignup && !isAuthenticated && route.name === "Signup") {
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (route.name === "Verify Email" && !isAuthenticated) {
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (isAuthenticated && !isVerified && route.name !== "Verify Email") {
          navigation.reset({
            index: 0,
            routes: [{ name: "Verify Email" }],
          });
          return;
        }

        if (isAuthenticated && isVerified) {
          const redirect = getDefaultAuthenticatedRoute({
            userRole: userInfo.userRole,
            isCompanionMode,
            courseModernizerOnlyEnabled: !!courseModernizerEnabled && !!courseModernizerOnlyEnabled,
          });
          navigation.reset({
            index: 0,
            routes: [{ name: redirect }],
          });
        }
      }, [
        allowFormSignup,
        courseModernizerEnabled,
        courseModernizerOnlyEnabled,
        isAuthenticated,
        isAuthReady,
        isCompanionMode,
        hasAuthCallbackTokens,
        isUpdating,
        isVerified,
        navigation,
        route.name,
        userInfo.userRole,
      ])
    );

    if (!isAuthReady || (isUpdating && !hasAuthCallbackTokens)) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return NoAuthComponent;
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default noAuth;
